'use client';

import { ChevronDown, HelpCircle } from 'lucide-react';
import { useState } from 'react';

export interface FAQItem {
  question: string;
  answer: string;
}

interface HubFAQProps {
  items: FAQItem[];
  title?: string;
}

function FAQItemComponent({ item, isOpen, onToggle }: { item: FAQItem; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-neutral-100 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between py-5 px-6 text-left gap-4 hover:bg-neutral-50 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="font-medium text-neutral-900 text-sm">{item.question}</span>
        <ChevronDown
          className={`w-4 h-4 text-neutral-400 flex-shrink-0 transition-transform duration-200 mt-0.5 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-[1000px]' : 'max-h-0'}`}
      >
        <p className="text-neutral-600 leading-relaxed text-sm px-6 pb-5 max-w-4xl">{item.answer}</p>
      </div>
    </div>
  );
}

export function HubFAQ({ items, title = 'Pertanyaan Umum' }: HubFAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  if (items.length === 0) return null;

  return (
    <section className="bg-white border-t border-neutral-200">
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-2 bg-yellow-100 rounded-lg">
            <HelpCircle className="w-5 h-5 text-yellow-600" />
          </div>
          <h2 className="text-xl font-bold text-neutral-900">{title}</h2>
        </div>
        <div className="bg-neutral-50 rounded-xl border border-neutral-200 overflow-hidden">
          {items.map((item, index) => (
            <FAQItemComponent
              key={index}
              item={item}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
